import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { normalSanatizerBRL } from "src/utils/functions";
import type { ResultsType } from "../calculadora-custos-fixos_d";

// Cores para cada categoria
const COLORS: { [key: string]: string } = {
  'Moradia': '#2563eb',           // azul
  'Transporte': '#7c3aed',        // roxo
  'Saúde & Bem-estar': '#dc2626', // vermelho
  'Alimentação': '#059669',       // verde escuro
  'Estilo de Vida': '#ea580c',   // laranja
  'Disponível': '#10b981',        // verde claro (destaque)
};

export const CustosFixosGraph = ({
  results,
}: {
  results: ResultsType;
}) => {
  
  // Preparar dados para o gráfico
  const chartData = [
    ...results.categorias.map(cat => ({
      name: cat.nome,
      value: cat.total,
      percentage: cat.percentual,
      color: COLORS[cat.nome] || '#6b7280'
    })),
    // Adicionar "Disponível" como fatia se houver valor
    ...(results.valorDisponivel > 0 ? [{
      name: 'Disponível',
      value: results.valorDisponivel,
      percentage: results.percentualDisponivel,
      color: COLORS['Disponível']
    }] : [])
  ];

  // Custom label para o gráfico
  const renderCustomLabel = (entry: any) => {
    return `${entry.percentage.toFixed(1)}%`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-1">{data.name}</p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Valor:</span> R$ {normalSanatizerBRL(data.value)}
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Percentual:</span> {data.percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-6">
        {payload.map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-700">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mb-6">
      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={120}
            innerRadius={70}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={2}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={renderLegend} />
        </PieChart>
      </ResponsiveContainer>

      {/* Legenda detalhada abaixo do gráfico */}
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold text-gray-800 mb-3 text-center">
          Resumo da Distribuição
        </h4>
        <div className="space-y-2">
          {chartData.map((item, index) => (
            <div 
              key={index} 
              className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0"
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm font-medium text-gray-700">
                  {item.name}
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-gray-800">
                  R$ {normalSanatizerBRL(item.value)}
                </span>
                <span className="text-xs text-gray-600 ml-2">
                  ({item.percentage.toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Total */}
        <div className="mt-4 pt-3 border-t-2 border-gray-300 flex justify-between items-center">
          <span className="font-bold text-gray-800">TOTAL</span>
          <span className="text-lg font-bold text-primary">
            R$ {normalSanatizerBRL(results.rendaLiquida)}
          </span>
        </div>
      </div>

      {/* Nota sobre o gráfico */}
      {results.valorDisponivel > 0 && (
        <div className="mt-4 p-3 bg-green-50 rounded border border-green-200">
          <p className="text-sm text-gray-700 text-center">
            <span className="font-semibold text-green-800">💡 Dica:</span> A fatia verde 
            representa o valor disponível para investimentos, imprevistos e qualidade de vida.
          </p>
        </div>
      )}
    </div>
  );
};